import React from "react";
import styled from "styled-components";
import Section from "../ui/Section/Section";
import { Grid } from "../ui/Grid/Grid";
import ChartCard from "../ui/Card/ChartCard";
import { CardBody, CardUnit, CardValue } from "../ui/Card/Card";
import GaugeStat from "../ui/GaugeStat/GaugeStat";
import SplitBar from "../ui/SplitBar/SplitBar";
import MiniMetric from "../ui/MiniMetric/MiniMetric";
import DeltaValue from "../ui/DeltaValue/DeltaValue";
import SectionSkeleton from "../ui/SectionSkeleton/SectionSkeleton";
import { INDICATORS, SECTIONS } from "../../constants";
import { fmt1, fmt1Signed } from "../../functions/format";
import { pctChange } from "../../functions/mapEffectif";
import { CHART_COLORS } from "../../styles/theme";
import useIndicatorsVisibility from "../../hooks/useIndicatorsVisibility";

const { id, title } = SECTIONS.temps;

const SKELETON_ROWS = [{ cols: 3, cards: [3, 3, 3] }];

/** Textes des tooltips "vs précédent" ; ajuster les suffixes ici. */
const formatPointDeltaTip = (current, previous) =>
    current !== null &&
    current !== undefined &&
    previous !== null &&
    previous !== undefined
        ? `${fmt1Signed(current - previous)} pt vs précédent`
        : undefined;

const formatPctDeltaTip = (current, previous) => {
    if (current === null || current === undefined) return undefined;
    const delta = pctChange(current, previous);
    return delta !== null ? `${fmt1Signed(delta)}% vs précédent` : undefined;
};

const TempsSection = ({ data, isLoading }) => {
    const { isVisible } = useIndicatorsVisibility();
    if (isLoading && !data) {
        return (
            <Section id={id} title={title}>
                <SectionSkeleton rows={SKELETON_ROWS} />
            </Section>
        );
    }

    if (!data) return null;

    const surTheorique = data.pctRealise > 100;

    return (
        <Section id={id} title={title}>
            <Grid cols={3}>
                {isVisible(INDICATORS.heures_realisees) && (
                    <ChartCard
                        indicator={INDICATORS.heures_realisees}
                        title="Heures réalisées"
                        fill
                    >
                        <Fill>
                            <GaugeStat
                                pct={Math.min(100, data.pctRealise)}
                                color={
                                    surTheorique
                                        ? CHART_COLORS.amber
                                        : CHART_COLORS.violet
                                }
                                unit="%"
                                sub="du théorique"
                                value={
                                    <DeltaValue
                                        section={id}
                                        tip={formatPointDeltaTip(
                                            data.pctRealise,
                                            data.pctRealisePrevious
                                        )}
                                    >
                                        {fmt1(data.pctRealise)}
                                    </DeltaValue>
                                }
                                tip={`${fmt1(
                                    data.pctRealise
                                )} % du théorique (${fmt1Signed(
                                    data.ecartTheorique
                                )} h)`}
                            />
                        </Fill>
                        <MiniMetric
                            mt="10px"
                            label="Écart vs théorique"
                            value={
                                <DeltaValue
                                    section={id}
                                    tip={formatPctDeltaTip(
                                        data.ecartTheorique,
                                        data.ecartTheoriquePrevious
                                    )}
                                >
                                    {`${fmt1Signed(data.ecartTheorique)} h`}
                                </DeltaValue>
                            }
                        />
                    </ChartCard>
                )}

                {isVisible(INDICATORS.heures_supplementaires) && (
                    <ChartCard
                        indicator={INDICATORS.heures_supplementaires}
                        title="Heures supplémentaires"
                        fill
                    >
                        <CardBody>
                            <CardValue>
                                {fmt1(data.heuresSup)}
                                <CardUnit>h</CardUnit>
                            </CardValue>
                        </CardBody>
                        <MiniMetric
                            mt="10px"
                            label="Cumul période"
                            value={
                                <DeltaValue
                                    section={id}
                                    tip={formatPctDeltaTip(
                                        data.heuresSup,
                                        data.heuresSupPrevious
                                    )}
                                >
                                    {`${fmt1(data.heuresSup)} h`}
                                </DeltaValue>
                            }
                        />
                    </ChartCard>
                )}

                {isVisible(INDICATORS.teletravail) && (
                    <ChartCard
                        indicator={INDICATORS.teletravail}
                        title="Télétravail"
                        fill
                    >
                        <Fill $center>
                            <SplitBar
                                noMargin
                                left={{
                                    label: "Télétravail",
                                    value: (
                                        <DeltaValue
                                            section={id}
                                            tip={formatPointDeltaTip(
                                                data.telePct,
                                                data.telePctPrevious
                                            )}
                                        >
                                            {`${fmt1(data.telePct)}%`}
                                        </DeltaValue>
                                    ),
                                    pct: data.telePct,
                                    tip: `Télétravail : ${fmt1(
                                        data.telePct
                                    )} %`,
                                }}
                                right={{
                                    label: "Présentiel",
                                    value: `${fmt1(data.presentielPct)}%`,
                                    tip: `Présentiel : ${fmt1(
                                        data.presentielPct
                                    )} %`,
                                }}
                            />
                        </Fill>
                        <MiniMetric
                            mt="10px"
                            label="Moyenne"
                            value={
                                <DeltaValue
                                    section={id}
                                    tip={formatPctDeltaTip(
                                        data.teleJours,
                                        data.teleJoursPrevious
                                    )}
                                >
                                    {`${fmt1(data.teleJours)} j/collab.`}
                                </DeltaValue>
                            }
                        />
                    </ChartCard>
                )}
            </Grid>
        </Section>
    );
};

export default TempsSection;

const Fill = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: ${({ $center }) => ($center ? "center" : "flex-start")};
`;
